import { defineStore } from 'pinia';
import api from '@/api/api';
import type { ZAssignment } from '@schemas/assignment.schemas';
import assignmentAPI from "@/api/assignment";
import type {AssignmentStatus} from "@typesDef/assignment.types";
import type {Id} from "@typesDef/general.types";
import {setIdsUnique} from "@/stores/helpers/storeHelper";

export const useAssignmentsStore = defineStore('assignments', {
    state: () => ({
        assignmentsById: {} as Record<Id, ZAssignment>,
        myAssignmentIds: [] as Id[],
        myAssignmentId: null as Id | null
    }),
    getters: {
        myAssignments: (state) => state.myAssignmentIds.map(id => state.assignmentsById[id]).filter(Boolean) as ZAssignment[],
        currentAssignment(state): ZAssignment | null {
            return state.myAssignmentId ? state.assignmentsById[state.myAssignmentId] ?? null : null;
        }
    },
    actions: {
        clearMyData(): void {
            // both are only used in the context of the current loggedin user
            this.assignmentsById = {};
            this.myAssignmentId = null;
            this.myAssignmentIds = [];
        },
        // FIXME move them to general library since they are used in multiple stores
        upsertMany(docs: ZAssignment[]) {
            for (const e of docs) this.assignmentsById[e._id] = e
        },

        async fetchAssignment(assignmentId: Id): Promise<ZAssignment> {
            log.debug(`fetchAssignment(${assignmentId})`);
            const cached = this.assignmentsById[assignmentId];
            if(cached) {
                this.myAssignmentId = assignmentId;
                return cached;
            }
            const doc = await api.getDocument<ZAssignment>('assignments', assignmentId);
            this.assignmentsById[doc._id] = doc;
            this.myAssignmentId = doc._id;
            return doc;
        },
        async fetchAssignments(query: Record<string, any> = {}): Promise<ZAssignment[]> {
            const docs = await api.getDocuments<ZAssignment>('assignments', query);
            this.upsertMany(docs);
            setIdsUnique(this.myAssignmentIds, docs.map(d => d._id));
            return docs;
        },
        async addAssignment(assignment: ZAssignment): Promise<ZAssignment> {
            const created = await api.postDocument<ZAssignment>('assignments', assignment);
            if(!created || !created._id) {
                throw new Error('Failed to create assignment');
            }
            this.assignmentsById[created._id] = created;
            setIdsUnique(this.myAssignmentIds, [created._id]);
            return created;
        },
        async updateAssignment(assignment: Partial<ZAssignment>): Promise<ZAssignment> {
            const updated = await api.putDocument<ZAssignment>('assignments', assignment);
            if(!updated || !updated._id) {
                throw new Error('Failed to update assignment');
            }
            this.assignmentsById[updated._id] = updated;
            this.myAssignmentId = updated._id; // set current assignment to the updated one
            return updated;
        },
        async updateStatus(assignmentId: string, status: AssignmentStatus): Promise<ZAssignment> {
            const updated = await assignmentAPI.updateStatus(assignmentId, status);
            if(!updated || !updated._id) {
                throw new Error('Failed to update assignment status');
            }
            this.assignmentsById[updated._id] = updated;
            this.myAssignmentId = updated._id; // set current assignment to the updated one
            return updated;
        },

        async removeAssignment(assignmentId: Id): Promise<void> {
            await api.deleteDocument<ZAssignment>('assignments', assignmentId);
            delete this.assignmentsById[assignmentId];
            this.myAssignmentIds = this.myAssignmentIds.filter(id => id !== assignmentId);
            if(this.myAssignmentId === assignmentId) {
                this.myAssignmentId = null;
            }
        }
    }
});
